import { useEffect, useRef, useState } from "react";
import { IProjectData } from "../models/shared/IProjectData";
import { SmartFilingManagerFluent9 } from "../services/SmartFiling/SmartFilingManagerFluent9";
import { Logger } from "../services/Logger";

export interface UseSuggestedProjectsOptions {
    smartFilingManager: SmartFilingManagerFluent9;
    myProjects: IProjectData[];
    logger: Logger;
    reloadToken?: number;
    maxSuggestions?: number;
}

export interface UseSuggestedProjectsResult {
    suggestedProjects: IProjectData[];
}

const DEFAULT_MAX_SUGGESTIONS = 3;

export function useSuggestedProjects(options: UseSuggestedProjectsOptions): UseSuggestedProjectsResult {
    const { smartFilingManager, myProjects, logger, reloadToken, maxSuggestions = DEFAULT_MAX_SUGGESTIONS } = options;

    const [suggestedProjects, setSuggestedProjects] = useState<IProjectData[]>([]);
    const resolvedRef = useRef(false);

    // Reset on reload
    useEffect(() => {
        if (reloadToken !== undefined && reloadToken > 0) {
            resolvedRef.current = false;
            setSuggestedProjects([]);
        }
    }, [reloadToken]);

    // Resolve suggested projects when myProjects become available
    useEffect(() => {
        if (resolvedRef.current || myProjects.length === 0) return;
        let cancelled = false;

        const fetchSuggestions = async () => {
            try {
                const results = await smartFilingManager.getSuggestedProjects(myProjects, false);
                if (cancelled) return;
                if (results.length) {
                    setSuggestedProjects(
                        results.slice(0, maxSuggestions).map((proj) => {
                            const originalProject = myProjects.find((p) => p.nrn === proj.nrn);
                            const { name, number, nrn, nixEnabled, isDisable: isDisabled, code } = proj;

                            return {
                                name,
                                number,
                                displayName: number ? `${number} - ${name}` : name,
                                nrn,
                                nixEnabled,
                                isDisabled,
                                code,
                                autoFileMailboxFolderName: originalProject?.autoFileMailboxFolderName ?? name,
                            };
                        })
                    );
                }
                resolvedRef.current = true;
            } catch (error) {
                logger.error("useSuggestedProjects - error retrieving suggested projects", error);
                resolvedRef.current = true;
            }
        };

        fetchSuggestions();
        return () => {
            cancelled = true;
        };
    }, [myProjects]);

    return {
        suggestedProjects,
    };
}
